import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wand2, Share2, Copy, Check, X, Mic, Loader2, Play } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const KEYS = [["bg", "Background"], ["panel", "Panel"], ["accent", "Accent"], ["text", "Text"]];

export function ColorPicker({ design, inv, set }) {
  const c = inv.colors || design.pal;
  return (
    <div data-testid="invite-colors">
      <p className="text-xs font-medium mb-2">Colours</p>
      <div className="flex flex-wrap items-center gap-3">
        {KEYS.map(([k, l]) => (
          <label key={k} className="flex items-center gap-1.5 text-[11px]"><input type="color" value={c[k]} onChange={(e) => set({ colors: { bg: c.bg, panel: c.panel, accent: c.accent, text: c.text, [k]: e.target.value } })} className="w-8 h-8 rounded cursor-pointer" data-testid={`invite-color-${k}`} />{l}</label>
        ))}
        {inv.colors && <button onClick={() => set({ colors: null })} className="text-xs underline" data-testid="invite-colors-reset">Use design colours</button>}
      </div>
    </div>
  );
}

export function AiWording({ inv, set }) {
  const [tone, setTone] = useState("romantic");
  const [busy, setBusy] = useState(false);
  async function go() {
    setBusy(true);
    try { const { data } = await api.post("/ai/invite-wording", { kind: inv.kind, tone }); set({ headline: data.headline, message: data.message }); toast.success("Wording written. Edit it any way you like."); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  return (
    <div className="flex items-center gap-2" data-testid="invite-ai-wording">
      <select value={tone} onChange={(e) => setTone(e.target.value)} className="input-luxe !w-auto !py-1.5 text-xs" data-testid="invite-ai-tone">
        {["romantic", "fairytale", "formal", "playful", "Hollywood glamour", "short and sweet"].map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <button onClick={go} disabled={busy} className="btn-outline-gold text-xs" data-testid="invite-ai-write">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Write it for me</button>
    </div>
  );
}

export function VoiceNarration({ inv, setInv }) {
  const [voices, setVoices] = useState(null);
  const [err, setErr] = useState("");
  const [vid, setVid] = useState(inv.voice_id || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/voice/voices").then((r) => { setVoices(r.data); if (!vid && r.data[0]) setVid(r.data[0].voice_id); }).catch((e) => setErr(formatApiError(e))); }, []); // eslint-disable-line
  async function make() {
    setBusy(true);
    try { const { data } = await api.post(`/weddings/mine/invites/${inv.invite_id}/narration`, { voice_id: vid }); setInv({ ...inv, ...data, voice_id: vid }); toast.success("Narration ready. It plays when the invite opens."); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  if (err) return <p className="text-xs text-[var(--muted)]" data-testid="invite-voice-off"><Mic className="w-3 h-3 inline" /> Voice narration: {err}</p>;
  if (!voices) return null;
  return (
    <div className="space-y-2">
      <VoicePick voices={voices} vid={vid} setVid={setVid} onGo={make} busy={busy} label={inv.narration_path ? "Re-record narration" : "Save this voice"} testid="invite-voice" />
      <VoiceSample vid={vid} saved={!!inv.narration_path} what="invite" request={() => api.post(`/weddings/mine/invites/${inv.invite_id}/narration/preview`, { voice_id: vid, text: [inv.headline, inv.message].filter(Boolean).join(". ").slice(0, 800) }, { responseType: "blob" })} />
    </div>
  );
}

export function VoiceSample({ vid, saved, what, request }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);
  async function hear() {
    setBusy(true);
    try {
      const { data } = await request();
      setUrl(URL.createObjectURL(data));
    } catch (e) {
      if (e.response?.data instanceof Blob) { try { e.response.data = JSON.parse(await e.response.data.text()); } catch { e.response.data = {}; } }
      toast.error(formatApiError(e) || "Couldn't make a sample");
    } finally { setBusy(false); }
  }
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="invite-voice-sample">
      <button onClick={hear} disabled={busy || !vid} className="btn-outline-gold text-xs" data-testid="invite-voice-sample-btn">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} Hear a sample</button>
      {url && <audio key={url} src={url} controls autoPlay className="h-8 w-52" data-testid="invite-voice-sample-audio" />}
      {saved && <span className="text-[11px] text-[var(--muted)] flex items-center gap-1" data-testid="invite-voice-saved"><Check className="w-3 h-3 text-green-600" /> Saved recording is ready</span>}
      <p className="w-full text-[11px] text-[var(--muted)]">Samples read the start of your {what}. Happy with it? Save to record the full message.</p>
    </div>
  );
}

export function VoicePick({ voices, vid, setVid, onGo, busy, label, testid }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      <select value={vid} onChange={(e) => setVid(e.target.value)} className="input-luxe !w-auto !py-1.5 text-xs" data-testid={`${testid}-select`}>
        {voices.map((v) => <option key={v.voice_id} value={v.voice_id}>{v.name}</option>)}
      </select>
      {voices.find((v) => v.voice_id === vid)?.preview_url && <audio src={voices.find((v) => v.voice_id === vid).preview_url} controls className="h-8 w-40" />}
      <button onClick={onGo} disabled={busy || !vid} className="btn-outline-gold text-xs" data-testid={`${testid}-go`}>{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mic className="w-3 h-3" />} {label}</button>
    </div>
  );
}

export function SocialShare({ inv, setInv }) {
  const [reqs, setReqs] = useState([]);
  const load = () => api.get("/weddings/mine/invite-requests").then((r) => setReqs(r.data.filter((x) => x.invite_id === inv.invite_id))).catch(() => {});
  useEffect(() => { load(); }, [inv.invite_id]); // eslint-disable-line
  const url = inv.social_token && `${window.location.origin}/s/${inv.social_token}`;
  async function toggle(on) {
    try { const { data } = await api.post(`/weddings/mine/invites/${inv.invite_id}/social?enabled=${on}`); setInv({ ...inv, ...data }); toast.success(on ? "Social link is on" : "Social link turned off"); } catch (e) { toast.error(formatApiError(e)); }
  }
  async function decide(id, action) {
    try { const { data } = await api.post(`/weddings/mine/invite-requests/${id}/${action}`); toast.success(action === "approve" ? `Approved and added to your guest list${data.delivered ? ". Their invite was sent." : ". Email is off, so share their link from Guests."}` : "Declined"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <div className="space-y-2 border-t border-[var(--line)] pt-4" data-testid="invite-social">
      <p className="text-xs font-medium flex items-center gap-1"><Share2 className="w-3 h-3" /> Share on social media</p>
      <p className="text-[11px] text-[var(--muted)]">People who open the social link see only your names. They can ask to join, and they see the date, venue, photos and RSVP only after you approve them.</p>
      {url ? (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => navigator.clipboard.writeText(url).then(() => toast.success("Social link copied"), () => toast.error("Couldn't copy"))} className="btn-outline-gold text-xs" data-testid="invite-social-copy"><Copy className="w-3 h-3" /> Copy social link</button>
          <button onClick={() => toggle(true)} className="text-xs underline" data-testid="invite-social-new">New link</button>
          <button onClick={() => toggle(false)} className="text-xs underline text-red-600" data-testid="invite-social-off">Turn off</button>
        </div>
      ) : <button onClick={() => toggle(true)} className="btn-outline-gold text-xs" data-testid="invite-social-on">Make a social link</button>}
      {reqs.map((r) => (
        <div key={r.request_id} className="flex items-center justify-between gap-2 text-sm rounded-lg bg-[var(--bg-warm)] px-3 py-2" data-testid={`invite-request-${r.request_id}`}>
          <span><b>{r.name}</b> <span className="text-xs text-[var(--muted)]">{r.email}{r.note ? ` · ${r.note}` : ""}</span></span>
          <span className="flex gap-1">
            <button onClick={() => decide(r.request_id, "approve")} className="p-1.5 rounded-full bg-emerald-600 text-white" data-testid={`invite-request-approve-${r.request_id}`}><Check className="w-3 h-3" /></button>
            <button onClick={() => decide(r.request_id, "decline")} className="p-1.5 rounded-full bg-red-600 text-white" data-testid={`invite-request-decline-${r.request_id}`}><X className="w-3 h-3" /></button>
          </span>
        </div>
      ))}
    </div>
  );
}
